import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Tabs, Tab, Typography, IconButton, Tooltip } from '@mui/material'
import { Close, CloseFullscreen } from '@mui/icons-material'
import { useTabStore, type TabItem } from '../../stores/tab.store'

export default function TabBar() {
  const { tabs, activeTab, setActiveTab, closeTab, clearTabs } = useTabStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [hoveredTab, setHoveredTab] = useState<string | null>(null)

  useEffect(() => {
    const currentTab = tabs.find((tab) => tab.path === location.pathname)
    if (currentTab && currentTab.path !== activeTab) {
      setActiveTab(currentTab.path)
    }
  }, [location.pathname, tabs, activeTab, setActiveTab])

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      setActiveTab(newValue)
      navigate(newValue)
    },
    [setActiveTab, navigate],
  )

  const handleClose = useCallback(
    (event: React.MouseEvent, path: string) => {
      event.stopPropagation()
      event.preventDefault()
      const wasActive = path === activeTab
      const closedIndex = tabs.findIndex((item) => item.path === path)
      const remainingTabs = tabs.filter((item) => item.path !== path)
      closeTab(path)
      if (wasActive && remainingTabs.length > 0) {
        const nextIndex = Math.min(closedIndex, remainingTabs.length - 1)
        const nextTab = remainingTabs[nextIndex]
        navigate(nextTab.path)
      } else if (wasActive && remainingTabs.length === 0) {
        navigate('/')
      }
    },
    [activeTab, closeTab, navigate, tabs],
  )

  if (tabs.length === 0) return null

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 1,
        minHeight: 40,
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, px: 2 } }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.path}
            value={tab.path}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                      {tab.objectLabel}
                    </Typography>
                {hoveredTab === tab.path && (
                  <Box
                    component="span"
                    data-close-tab={tab.path}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      ml: 0.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      handleClose(event, tab.path)
                    }}
                  >
                    <Close sx={{ fontSize: 12 }} />
                  </Box>
                )}
              </Box>
            }
            onMouseEnter={() => setHoveredTab(tab.path)}
            onMouseLeave={() => setHoveredTab(null)}
            sx={{ textTransform: 'none' }}
          />
        ))}
      </Tabs>
      {tabs.length > 1 && (
        <Tooltip title="Close All">
          <IconButton
            size="small"
            sx={{ ml: 'auto', my: 'auto', px: 0.5 }}
            onClick={() => {
              clearTabs()
              navigate('/')
            }}
          >
            <CloseFullscreen sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}
